// client/src/api/customValues.js
import { proxyGet, proxyPost, proxyPut } from "./proxy";

const DEBUG_CUSTOM_VALUES = /^(1|true|yes|on)$/i.test(String(import.meta.env.VITE_DEBUG_CUSTOM_VALUES || import.meta.env.VITE_DEBUG_API || ""));

const redactHeaders = (headers = {}) => {
  const out = { ...(headers || {}) };
  for (const k of Object.keys(out)) {
    if (/authorization|token|api[-_]?key|secret|cookie/i.test(k)) out[k] = "[redacted]";
  }
  return out;
};

const logCV = (event, payload) => {
  if (!DEBUG_CUSTOM_VALUES) return;
  console.info(`[customValues] ${event}`, payload);
};

const inflightCustomValuesRequests = new Map();

const toRequestKey = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params || {})
    .sort()
    .forEach((key) => {
      const value = params[key];
      if (value == null) return;
      query.append(key, String(value));
    });
  const q = query.toString();
  return q ? `${path}?${q}` : path;
};

// const EDGE_ORIGIN = process.env.REACT_APP_EDGE_ORIGIN || "https://api.yebo-ai.com";
//const BASE_URL = `${EDGE_ORIGIN}`;

// -------- key normalization --------
const normalizeKey = (k = "") =>
  String(k).trim().toLowerCase()
    .replace(/^\{\{\s*custom_values\./, "")
    .replace(/\s*\}\}$/, "");

const keyEquals = (cvKey, wanted) => {
  const raw = String(cvKey || "");
  const want = normalizeKey(wanted);
  return raw === wanted || normalizeKey(raw) === want || raw === `{{ custom_values.${want} }}`;
};

// -------- value compare (for verify) --------
function looselyEqualValues(a, b) {
  const A = a == null ? "" : String(a).trim();
  const B = b == null ? "" : String(b).trim();
  if (!A && !B) return true;
  try {
    const pjA = JSON.parse(A);
    const pjB = JSON.parse(B);
    return JSON.stringify(pjA) === JSON.stringify(pjB);
  } catch {
    return A === B;
  }
}

function stringifyCvValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function isBlankValue(value) {
  return value == null || String(value).trim() === "";
}

// Fallback name from a key (for safety)
function humanizeKey(key) {
  const k = normalizeKey(key);
  return k.replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

// -------- API --------
export function listAllCustomValues(locationId, opts = {}) {
  const { caller = "unknown", ...proxyOpts } = opts || {};
  const params = { ...(opts.params || {}), locationId };
  const path = `/locations/${locationId}/customValues`;
  const requestKey = toRequestKey(path, params);
  if (inflightCustomValuesRequests.has(requestKey)) {
    logCV("list:deduped", { requestKey, caller });
    return inflightCustomValuesRequests.get(requestKey);
  }

  const req = proxyGet(path, { ...proxyOpts, params }).then((data) => {
    logCV("list:response", {
      requestKey,
      caller,
      locationId,
      count: Array.isArray(data?.customValues) ? data.customValues.length : 0,
      keys: data ? Object.keys(data) : [],
    });
    return data;
  }).finally(() => {
    inflightCustomValuesRequests.delete(requestKey);
  });

  logCV("list:request", {
    requestKey,
    caller,
    path,
    locationId,
    params,
    headers: opts.headers ? redactHeaders(opts.headers) : undefined,
  });
  inflightCustomValuesRequests.set(requestKey, req);
  return req;
}

export async function findCustomValueByFieldKey(locationId, fieldKey, opts = {}) {
  const data = await listAllCustomValues(locationId, opts);
  const list = data?.customValues || [];
  return list.find((cv) => keyEquals(cv.fieldKey, fieldKey)) || null;
}

// ⬅️ include locationId AND send { name, value } in the body
export function updateCustomValue(locationId, id, value, opts = {}) {
  const body = { value };
  if (opts.name) body.name = String(opts.name);
  const path = `/locations/${locationId}/customValues/${id}`;
  logCV("update:request", {
    path,
    locationId,
    id,
    body,
    headers: opts.headers ? redactHeaders(opts.headers) : undefined,
  });
  return proxyPut(path, body, opts).then((data) => {
    logCV("update:response", {
      locationId,
      id,
      ok: Boolean(data?.ok ?? true),
      keys: data ? Object.keys(data) : [],
    });
    return data;
  }).catch((err) => {
    logCV("update:error", {
      locationId,
      id,
      status: err?.status,
      message: String(err?.message || err),
      body: err?.body,
      requestId: err?.requestId,
    });
    throw err;
  });
}

export function createCustomValue(locationId, { name, fieldKey, value }, opts = {}) {
  if (!locationId) throw new Error("locationId is required");
  if (!fieldKey) throw new Error("fieldKey is required");

  const normalized = normalizeKey(fieldKey);
  const resolvedName = String(name || normalized || humanizeKey(fieldKey)).trim();
  const body = {
    name: resolvedName,
    value: stringifyCvValue(value),
  };

  const path = `/locations/${locationId}/customValues`;
  logCV("create:request", {
    path,
    locationId,
    body: {
      ...body,
      value: body.value.length > 200 ? `${body.value.slice(0, 200)}…` : body.value,
    },
    headers: opts.headers ? redactHeaders(opts.headers) : undefined,
  });

  return proxyPost(path, body, opts).then((data) => {
    logCV("create:response", {
      locationId,
      fieldKey,
      name: resolvedName,
      ok: Boolean(data?.ok ?? true),
      keys: data ? Object.keys(data) : [],
      createdFieldKey: data?.customValue?.fieldKey || "",
    });
    return data;
  }).catch((err) => {
    logCV("create:error", {
      locationId,
      fieldKey,
      name: resolvedName,
      status: err?.status,
      message: String(err?.message || err),
      body: err?.body,
      requestId: err?.requestId,
    });
    throw err;
  });
}

/**
 * Update by field key (raw or mustache).
 * opts:
 *  - name?: string  -> label to satisfy LC validator (falls back to CV.name)
 *  - verify?: bool  -> read-after-write check
 *  - ...            -> passed to proxy (headers, timeout)
 */
export async function updateCustomValueByFieldKey(locationId, fieldKey, value, opts = {}) {
  const rec = await findCustomValueByFieldKey(locationId, fieldKey, opts);
  if (!rec?.id) throw new Error(`Custom value not found for key: ${fieldKey}`);

  const name = opts.name ?? rec.name ?? humanizeKey(fieldKey);

  const res = await updateCustomValue(locationId, rec.id, value, { ...opts, name });

  if (opts.verify) {
    const fresh = await findCustomValueByFieldKey(locationId, fieldKey, opts);
    if (!looselyEqualValues(fresh?.value, value)) {
      throw new Error(`Verification failed for "${fieldKey}": server value did not match`);
    }
  }
  return res;
}

/**
 * Ensure a set of Custom Values exist for a location.
 * - Creates missing field keys.
 * - Optionally fills existing empty values.
 */
export async function ensureCustomValues(locationId, values = [], opts = {}) {
  if (!locationId) throw new Error("locationId is required");
  const list = Array.isArray(values) ? values : [];
  const fillEmpty = opts.fillEmpty !== false;
  const verify = Boolean(opts.verify);
  const { caller = "ensureCustomValues", ...proxyOpts } = opts;

  const summary = {
    created: [],
    updated: [],
    skipped: [],
    failed: [],
    totals: {
      requested: list.length,
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0,
    },
    ok: true,
  };

  if (!list.length) return summary;

  const existing = await listAllCustomValues(locationId, {
    ...proxyOpts,
    caller,
  });
  const byNormKey = new Map();
  for (const cv of existing?.customValues || []) {
    const norm = normalizeKey(cv?.fieldKey || "");
    if (!norm || byNormKey.has(norm)) continue;
    byNormKey.set(norm, cv);
  }

  for (const item of list) {
    const fieldKey = String(item?.fieldKey || "").trim();
    const name = String(item?.name || humanizeKey(fieldKey));
    const desiredValue = stringifyCvValue(item?.value);

    if (!fieldKey) {
      summary.failed.push({ fieldKey: "", reason: "Missing fieldKey" });
      continue;
    }

    try {
      const norm = normalizeKey(fieldKey);
      const rec = byNormKey.get(norm);

      if (!rec?.id) {
        const createName = norm || name;
        const created = await createCustomValue(locationId, {
          fieldKey,
          name: createName,
          value: desiredValue,
        }, proxyOpts);

        const createdRecord = created?.customValue;
        let createdNorm = normalizeKey(createdRecord?.fieldKey || "");
        if (!createdNorm) createdNorm = norm;

        let fresh = null;
        if (createdNorm !== norm) {
          fresh = await findCustomValueByFieldKey(locationId, fieldKey, proxyOpts);
        }

        const effective = fresh || createdRecord || { fieldKey, name: createName, value: desiredValue };
        const effectiveNorm = normalizeKey(effective?.fieldKey || fieldKey || "");
        byNormKey.set(effectiveNorm || norm, effective);

        if (effectiveNorm && effectiveNorm !== norm) {
          throw new Error(
            `Created key mismatch for "${fieldKey}" (created "${effective.fieldKey || "unknown"}").` +
            ` Use name "${norm}" in GHL to match expected field key.`,
          );
        }

        summary.created.push({
          fieldKey,
          name,
          id: effective?.id,
        });
        continue;
      }

      if (!fillEmpty || !isBlankValue(rec.value) || isBlankValue(desiredValue)) {
        summary.skipped.push({ fieldKey, name, reason: "Already exists" });
        continue;
      }

      await updateCustomValue(locationId, rec.id, desiredValue, { ...proxyOpts, name });

      if (verify) {
        const fresh = await findCustomValueByFieldKey(locationId, fieldKey, proxyOpts);
        if (!looselyEqualValues(fresh?.value, desiredValue)) {
          throw new Error(`Verification failed for "${fieldKey}"`);
        }
      }

      summary.updated.push({ fieldKey, name, id: rec.id });
    } catch (err) {
      summary.failed.push({
        fieldKey,
        name,
        reason: String(err?.message || err),
      });
    }
  }

  summary.totals.created = summary.created.length;
  summary.totals.updated = summary.updated.length;
  summary.totals.skipped = summary.skipped.length;
  summary.totals.failed = summary.failed.length;
  summary.ok = summary.totals.failed === 0;
  return summary;
}

// Helper used by printout
export async function fetchPrintConfigFields(locationId, opts = {}) {
  const rec = await findCustomValueByFieldKey(locationId, "print_config", opts);
  if (!rec?.value) return [];
  return String(rec.value).split("\n").map((s) => s.trim()).filter(Boolean);
}
